import React, { useEffect, useContext, useMemo, memo, useState } from "react";
import { ThemeContext } from "../../../../context";

import { withRouter, RouteComponentProps } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { performancePages } from "../../../../constants";

import PmoTooltip from "../../../ProductMarketingOptimization/components/common/PmoTooltip";
import CompetitorSelect from "../competitorSelect";
import { ErrorBoundary } from "@sentry/react";
import { ErrorMsg, NoDataMsg } from "../../../../components/AppMessages";
import LoadingEllipsis from "../../../../components/Loading/LoadingEllipsis";

import { getBrandKPISummary } from "../../../../api/brandPerformanceApi";
import { RootStateOrAny, useSelector } from "react-redux";
import BrandKPISummaryBarGraph from "../../../../components/Charts/BrandPerformance/BrandKPISummaryBarGraph";
import { BrandKPISummaryTable } from "../../../../components/Tables/subs/BrandKPISummaryTable";
import { isNullOrUndefined } from "../../../../utils/utilityFunctions";

interface Props extends RouteComponentProps {
  onHandleRadioClick: any;
  nissanCompetitor: string;
  activePage: performancePages;
  diff: number;
  granularity: string;
}

const BrandKPISummaryContent = withRouter((props: Props) => {
  const { onHandleRadioClick, nissanCompetitor, activePage, diff, history, granularity } = props;

  const params = useSelector((state: RootStateOrAny) => state.parameters);
  const { granularityParam, market } = params;
  const { theme } = useContext(ThemeContext);
  const [isLoadingChartSortingLogic, setIsLoadingChartSortingLogic] = useState(false);

  // fetch the trend and average data
  const {
    data: brandKPISummaryData,
    isFetching: loadingbrandKPISummaryData, // call data using react-query
    refetch: refetchBrandKPISummaryData,
  } = useQuery(
    ["brandKPISummaryData", granularity, history.location.search],
    () => getBrandKPISummary({ ...params, nissan_competitor: nissanCompetitor, performance_page: activePage }),
    { enabled: false }
  );
  const MemoizedBrandKPISummaryBarGraph = memo(BrandKPISummaryBarGraph);
  // when the url changes, refetch the data
  useEffect(() => {
    refetchBrandKPISummaryData();
  }, [history.location.search]);

  // Memoize the Cell component
  const MemoizedCell = memo((props: { cell: { row: { original: any } } }) => {
    const {
      cell: {
        row: { original },
      },
    } = props;

    return (
      <div className="bargraph-table-container">
        {loadingbrandKPISummaryData || isLoadingChartSortingLogic ? (
          <LoadingEllipsis isLoading={true} />
        ) : (
          <MemoizedBrandKPISummaryBarGraph
            data={[original]}
            granularity={granularity ? granularity : granularityParam}
            theme={theme}
            chartId={`kpi-summary-bar-graph-${original?.date}-${original?.market}-${original?.nissan_rank}`}
            percentageChart={false}
            market={market}
            diff={diff}
            setIsLoadingChartSortingLogic={setIsLoadingChartSortingLogic}
          />
        )}
      </div>
    );
  });

  // Add display name for better debugging
  MemoizedCell.displayName = "MemoizedCell";

  const columns = useMemo(
    () => [
      {
        id: "country",
        Header: `Country `,
        accessor: "market",
        defaultCanSort: false,
        Cell: (props1: { value: string }) => {
          const { value } = props1;

          return value;
        },
      },
      {
        Header: "Date KPI",
        accessor: `date`,
        id: "date_kpi",
        defaultCanSort: true,
        defaultSortDesc: false,
        Cell: (props1: { value: string }) => {
          const { value } = props1;

          return value;
        },
      },
      {
        Header: "Nissan Score (%)",
        accessor: `nissan_score`,
        id: "nissan_score",
        defaultCanSort: false,
        Cell: (props: { cell: { row: { original: any } } }) => {
          const {
            cell: {
              row: { original },
            },
          } = props;
          const value = original?.["nissan_score"];
          return !isNullOrUndefined(value) ? `${value}%` : "n/a";
        },
      },
      {
        Header: "Nissan Rank",
        accessor: `nissan_rank`,
        id: "nissan_rank",
        defaultCanSort: false,
        Cell: (props1: { value: string }) => {
          const { value } = props1;

          return value;
        },
      },
      {
        Header: "Brand Count",
        accessor: `brand_count`,
        id: "brand_count",
        defaultCanSort: false,
        Cell: (props1: { value: string }) => {
          const { value } = props1;

          return value;
        },
      },
      {
        Header: "Gaps",
        accessor: `gaps`,
        id: "gaps",

        defaultCanSort: false,
        Cell: (props1: { value: string[] }) => {
          const { value } = props1;
          return (
            <div>
              {value.map((gap: string) => (
                <div key={gap}>{gap}</div>
              ))}
            </div>
          );
        },
      },
      {
        Header: "Rank",
        accessor: `ranks`,
        id: "ranks",

        defaultCanSort: false,
        Cell: (props1: { value: string[] }) => {
          const { value } = props1;
          return (
            <div>
              {value.map((rank: string) => (
                <div key={rank}>{rank}</div>
              ))}
            </div>
          );
        },
      },

      {
        Header: "Brand",
        accessor: `brands`,
        id: "brands",

        defaultCanSort: false,
        Cell: (props1: { value: string[] }) => {
          const { value } = props1;
          return (
            <div>
              {value.map((brand: string) => (
                <div key={brand}>{brand}</div>
              ))}
            </div>
          );
        },
      },
      {
        Header: "Score (%)",
        accessor: `scores`,
        id: "scores",
        defaultCanSort: false,
        Cell: (props1: { value: string[] }) => {
          const { value } = props1;
          return (
            <div style={{ textAlign: "right" }}>
              {value.map((score: string) => (
                <div key={score}>{score}</div>
              ))}
            </div>
          );
        },
      },

      {
        Header: () => (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
            <div style={{ width: "2px", height: "20px", backgroundColor: "#d94e3d", marginRight: ".5em" }}></div>
            <span>Nissan</span>
          </div>
        ),
        accessor: ``,
        id: "graph",
        defaultCanSort: false,
        width: 300,
        Cell: MemoizedCell,
      },
    ],
    [loadingbrandKPISummaryData, granularity, granularityParam, theme, market, diff]
  );

  return (
    <div className="brand-KPI-summary-main-content">
      <div className="primary_background ">
        <div className="header" style={{ paddingTop: "1em", paddingLeft: "1em" }}>
          <div className="title_container">
            <h3>Brand KPI Summary</h3>
            <CompetitorSelect onHandleRadioClick={onHandleRadioClick} nissanCompetitor={nissanCompetitor} />
          </div>
          <div>
            <div className="kpi-summary-table-title-container">
              <p className="kpi-trend-metric-title nissan">Nissan</p>
              <p className="kpi-trend-metric-title top-3">TOP 3</p>
            </div>
            <div className="content">
              <div className="chart_content brand_perf_table" style={{ margin: "1em" }}>
                <ErrorBoundary fallback={<ErrorMsg />}>
                  <>
                    {brandKPISummaryData?.data?.length > 0 ? (
                      <>
                        <BrandKPISummaryTable
                          columns={columns ?? []}
                          data={brandKPISummaryData.data}
                          id="brand_market_share_table"
                          data-test-id="brand_market_share_table"
                          initialSortColId={{ id: "date", desc: true }}
                          hiddenCols={[]}
                          extraClass="appRatingsComments"
                          disableSortBy={true}
                        />
                      </>
                    ) : loadingbrandKPISummaryData ? (
                      <LoadingEllipsis isLoading={loadingbrandKPISummaryData} />
                    ) : (
                      <NoDataMsg />
                    )}
                  </>
                </ErrorBoundary>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default BrandKPISummaryContent;
